import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Container, Typography, CircularProgress } from '@mui/material';

// Custom hook
import useAccountSettings from '../hooks/useAccountSettings';

// Components
import AccountTabs from '../components/account/AccountTabs';
import ProfileForm from '../components/account/ProfileForm';
import PasswordForm from '../components/account/PasswordForm';
import NotificationPreferences from '../components/account/NotificationPreferences';
import LanguageSettings from '../components/account/LanguageSettings';
import FeedbackSnackbar from '../components/account/FeedbackSnackbar';

/**
 * Account Settings Page component
 * Allows users to manage their account settings, profile, and preferences
 * @returns {JSX.Element} The AccountSettingsPage component
 */
const AccountSettingsPage = () => {
  const { t } = useTranslation('user');
  const [activeTab, setActiveTab] = useState(0);

  const {
    loading,
    saving,
    error,
    success,
    formData,
    handleInputChange,
    handleLanguageChange,
    handleProfileSave,
    handlePasswordChange,
    handleNotificationSave,
    handleSnackbarClose,
    handleCountryChange,
  } = useAccountSettings();

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  if (loading) {
    return (
      <Container sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container
      maxWidth="lg"
      sx={{ py: 4 }}
    >
      <Typography
        variant="h4"
        component="h1"
        gutterBottom
      >
        {t('account.settings')}
      </Typography>

      <AccountTabs
        activeTab={activeTab}
        handleTabChange={handleTabChange}
      >
        {/* Profile Tab */}
        {activeTab === 0 && (
          <ProfileForm
            formData={formData}
            handleInputChange={handleInputChange}
            handleCountryChange={handleCountryChange}
            handleSubmit={handleProfileSave}
            saving={saving}
          />
        )}

        {/* Password Tab */}
        {activeTab === 1 && (
          <PasswordForm
            formData={formData}
            handleInputChange={handleInputChange}
            handleSubmit={handlePasswordChange}
            saving={saving}
          />
        )}

        {/* Notifications Tab */}
        {activeTab === 2 && (
          <NotificationPreferences
            formData={formData}
            handleInputChange={handleInputChange}
            handleSubmit={handleNotificationSave}
            saving={saving}
          />
        )}

        {/* Language Tab */}
        {activeTab === 3 && (
          <LanguageSettings
            formData={formData}
            handleLanguageChange={handleLanguageChange}
          />
        )}
      </AccountTabs>

      {/* Success/Error Snackbar */}
      <FeedbackSnackbar
        success={success}
        error={error}
        onClose={handleSnackbarClose}
      />
    </Container>
  );
};

export default AccountSettingsPage;
